import {DragHandle} from '@primer/react'
import type {RefObject} from 'react'
import {useCallback, useRef} from 'react'

import styles from './SidebarResizeHandle.module.css'

const SIDEBAR_WIDTH_VAR = '--sidebar-width'
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export interface SidebarResizeHandleProps {
  /** The sidebar element whose `--sidebar-width` custom property is driven. */
  sidebarRef: RefObject<HTMLElement | null>
  /**
   * The slider node. Shared with `usePaneWidth` so Primer's `DragHandle` and the
   * width hook operate on the same element.
   */
  handleRef: RefObject<HTMLDivElement | null>
  minWidth: number
  /**
   * The effective maximum width, capped to the viewport. Used only for the
   * slider's ARIA `aria-valuemax`; live clamping during a drag uses
   * {@link getMaxWidth} so it tracks viewport changes.
   */
  maxWidth: number
  /**
   * Returns the current effective max width (viewport-constrained). Called at
   * drag start and on every keyboard step so the handle never lets the sidebar
   * grow past the viewport-aware limit (`usePaneWidth.getMaxPaneWidth`).
   */
  getMaxWidth: () => number
  /** Committed width, used for the initial ARIA `valuenow`. */
  currentWidth: number
  /** Live width mutated during a drag to avoid per-frame re-renders. */
  currentWidthRef: RefObject<number>
  /** Persist the final width when a resize operation ends (`usePaneWidth.saveWidth`). */
  onResizeEnd: (width: number) => void
  /** Returns the width to reset to on double-click (`usePaneWidth.getDefaultWidth`). */
  onReset: () => number
}

/**
 * A pointer- and keyboard-accessible drag handle for resizing a
 * {@link CollapsibleSidebar}. Thin wrapper over `@primer/react`'s `DragHandle`:
 * that component owns the pointer capture, rAF throttling, arrow-key stepping,
 * double-click reset and slider ARIA; this wrapper only translates its
 * interaction callbacks into writes of the `--sidebar-width` custom property and
 * commits the final width. The handle sits on the sidebar's trailing (right)
 * edge, so a rightward drag / ArrowRight grows the sidebar (`position: 'start'`).
 */
export function SidebarResizeHandle({
  sidebarRef,
  handleRef,
  minWidth,
  maxWidth,
  getMaxWidth,
  currentWidth,
  currentWidthRef,
  onResizeEnd,
  onReset,
}: SidebarResizeHandleProps) {
  const dragStartClientXRef = useRef(0)
  const dragStartWidthRef = useRef(0)
  // Max captured at drag start so a pointer drag clamps against a stable value;
  // keyboard steps re-read the live max via `getMaxWidth()`.
  const dragMaxWidthRef = useRef(maxWidth)

  // Write the width to the DOM without a re-render, and keep the slider's ARIA
  // values in sync (DragHandle's `aria-valuenow` prop only updates on commit).
  const applyWidth = useCallback(
    (width: number) => {
      currentWidthRef.current = width
      sidebarRef.current?.style.setProperty(SIDEBAR_WIDTH_VAR, `${width}px`)
      const handle = handleRef.current
      if (handle) {
        const rounded = Math.round(width)
        handle.setAttribute('aria-valuenow', String(rounded))
        handle.setAttribute('aria-valuetext', `${rounded} pixels`)
      }
    },
    [sidebarRef, handleRef, currentWidthRef],
  )

  const handleDragStart = useCallback(
    (clientX: number) => {
      dragStartClientXRef.current = clientX
      dragStartWidthRef.current = sidebarRef.current?.getBoundingClientRect().width ?? currentWidthRef.current
      dragMaxWidthRef.current = getMaxWidth()
    },
    [sidebarRef, currentWidthRef, getMaxWidth],
  )

  // Keyboard passes a signed step delta; pointer passes the absolute cursor X,
  // measured against the drag-start position. Clamp against the viewport-aware
  // max: the live value for keyboard (viewport may have changed between steps),
  // the drag-start snapshot for pointer moves.
  const handleDrag = useCallback(
    (value: number, isKeyboard: boolean) => {
      const maxAllowed = isKeyboard ? getMaxWidth() : dragMaxWidthRef.current
      const nextWidth = isKeyboard
        ? clamp(currentWidthRef.current + value, minWidth, maxAllowed)
        : clamp(dragStartWidthRef.current + (value - dragStartClientXRef.current), minWidth, maxAllowed)
      if (Math.round(nextWidth) === Math.round(currentWidthRef.current)) return
      applyWidth(nextWidth)
    },
    [minWidth, getMaxWidth, currentWidthRef, applyWidth],
  )

  const handleDragEnd = useCallback(() => {
    onResizeEnd(currentWidthRef.current)
  }, [onResizeEnd, currentWidthRef])

  const handleDoubleClick = useCallback(() => {
    const resetWidth = clamp(onReset(), minWidth, getMaxWidth())
    applyWidth(resetWidth)
    onResizeEnd(resetWidth)
  }, [onReset, minWidth, getMaxWidth, onResizeEnd, applyWidth])

  return (
    <div className={styles.handleTrack}>
      <DragHandle
        handleRef={handleRef as RefObject<HTMLDivElement>}
        dragTargetRef={sidebarRef}
        data-component="CollapsibleSidebar.ResizeHandle"
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={Math.round(currentWidth)}
        formatValueText={width => `${width} pixels`}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onDoubleClick={handleDoubleClick}
      />
    </div>
  )
}
